// ADIB (Abu Dhabi Islamic Bank) — Covered Cashback Card.
// Two-row transaction format: merchant text at x≈162 on the row ABOVE the date row.
// Date row: postDate@38 | trxnDate@99 | [inlineDesc@162] | amount@~508 | [CR@537]
// Some rows (Cashback, Profit credit) carry an inline description directly on the date row.
// Summary lives on page 2 (page 1 carries only the statement date).
// Islamic pair: "Profit earned and bonus" (credit) + "Monthly Murabaha Payment" (debit) → 'islamic_net'.
import { toNum, dmyToISO } from '../util.js';

export function parseADIB(pages) {
  const p1 = pages[0] || [];
  const p2 = pages[1] || [];

  // Statement date: page 1, a DD/MM/YYYY at x≈377 in the card-header band
  let statementDate = null;
  for (const it of p1) {
    if (it.x >= 365 && it.x <= 390 && /^\d{2}\/\d{2}\/\d{4}$/.test(it.s)) {
      statementDate = dmyToISO(it.s); break;
    }
  }

  const summary = {
    statementDate,
    dueDate: null, minimumPayment: null, creditLimit: null, availableLimit: null, closingBalance: null,
    previousBalance: null,
  };

  // Page 2 summary: cover amount@60, available@148, monthly payment@239, payment date@318,
  // previous balance@406, present balance@491
  const byY2 = {};
  for (const it of p2) { const k = Math.round(it.y / 3) * 3; (byY2[k] = byY2[k] || []).push(it); }
  for (const y of Object.keys(byY2).map(Number).sort((a, b) => b - a)) {
    const cells = byY2[y].sort((a, b) => a.x - b.x);
    const coverCell = cells.find(c => c.x >= 52 && c.x <= 72 && /^[\d,]+\.\d{2}$/.test(c.s));
    const availCell = cells.find(c => c.x >= 138 && c.x <= 160 && /^[\d,]+\.\d{2}$/.test(c.s));
    const dueDateCell = cells.find(c => c.x >= 308 && c.x <= 330 && /^\d{2}\/\d{2}\/\d{4}$/.test(c.s));
    const closeCell = cells.find(c => c.x >= 480 && c.x <= 502 && /^[\d,]+\.\d{2}$/.test(c.s));
    if (coverCell && availCell) {
      summary.creditLimit    = toNum(coverCell.s);
      summary.availableLimit = toNum(availCell.s);
      const minCell = cells.find(c => c.x >= 228 && c.x <= 252 && /^[\d,]+\.\d{2}$/.test(c.s));
      if (minCell)   summary.minimumPayment = toNum(minCell.s);
      if (dueDateCell) summary.dueDate = dmyToISO(dueDateCell.s);
      if (closeCell)  summary.closingBalance = toNum(closeCell.s);
      const prevCell = cells.find(c => c.x >= 396 && c.x <= 418 && /^[\d,]+\.\d{2}$/.test(c.s));
      if (prevCell)  summary.previousBalance = toNum(prevCell.s);
      break;
    }
  }

  const txns = [];
  for (const page of pages) {
    const byY = {};
    for (const it of page) { const k = Math.round(it.y / 3) * 3; (byY[k] = byY[k] || []).push(it); }
    const sortedYs = Object.keys(byY).map(Number).sort((a, b) => b - a);

    for (let i = 0; i < sortedYs.length; i++) {
      const y = sortedYs[i];
      const cells = byY[y].sort((a, b) => a.x - b.x);

      // Date row: first two cells are DD/MM/YYYY at x≈38 and x≈99
      const postCell = cells.find(c => c.x >= 30 && c.x <= 50 && /^\d{2}\/\d{2}\/\d{4}$/.test(c.s));
      const trxnCell = cells.find(c => c.x >= 90 && c.x <= 112 && /^\d{2}\/\d{2}\/\d{4}$/.test(c.s));
      if (!postCell || !trxnCell) continue;

      // Amount: rightmost numeric at x>480
      const amountCell = [...cells].reverse().find(c => c.x > 480 && /^[\d,]+\.\d{2}$/.test(c.s));
      if (!amountCell) continue;
      const crCell = cells.find(c => c.x > 526 && c.s === 'CR');
      const credit = !!crCell;

      // Inline description: cells at x≈162–480 on this row, excluding amount and CR
      const inlineCells = cells.filter(c => c.x >= 155 && c.x < 480 && c !== amountCell && c !== crCell);
      let desc = inlineCells.map(c => c.s).join(' ').replace(/\s{2,}/g, ' ').trim();

      // No inline desc → look at the row directly above (merchant row)
      if (!desc) {
        // The merchant row is at a higher y (1–10 pts above)
        const prevY = sortedYs[i - 1];
        if (prevY !== undefined && prevY > y && prevY <= y + 12) {
          const merchantCells = (byY[prevY] || []).filter(c => c.x >= 155 && c.x < 480);
          desc = merchantCells.map(c => c.s).join(' ').replace(/\s{2,}/g, ' ').trim();
        }
      }
      if (!desc) continue;

      const amount = toNum(amountCell.s);
      if (amount == null) continue;

      const tag = /CASHBACK/i.test(desc) ? 'cashback'
        : /IPP\s+CARD|INSTALLMENT.*PLAN/i.test(desc) ? 'payment'
        : /PROFIT.*BONUS|MURABAHA|WAKALA/i.test(desc) ? 'islamic_net'
        : null;

      txns.push({
        date: dmyToISO(trxnCell.s),
        postingDate: dmyToISO(postCell.s),
        description: desc,
        amount,
        credit,
        tag,
      });
    }
  }

  return { bank: 'ADIB', summary, transactions: txns };
}
